/**
 * Grabs the data we dump from the server into the DOM and exports them as a singleton.
 * This allows us to conveniently dump server data into the dom and use this to load
 * that data into our app.
 */
// because the way webpack is implemented, you can import this file anywhere
// in the codebase like so:
// import serverData from 'utils/server-data'
import { WorldReady } from '@paypalcorp/worldready'
import { getMockQueryParams, getQueryParam, appendQueryParams } from './query'
import { get } from 'lodash-es'
import { VIEW_NAMES } from '@/constants/common'
import { PRODUCT_NAMES } from 'universal/cfs-card'

const { MAIN_VIEW_NAME, MODAL_VIEW_NAME } = VIEW_NAMES
const { CONSUMER_DEBIT_CARD } = PRODUCT_NAMES

const serverData: Record<string, any> = {}

const ENV_DEVELOPMENT = 'development'
const ENV_PRODUCTION = 'production'

function initServerData(data) {
  // For mocking the state in doc
  if (data) {
    Object.assign(serverData, data)
    return data
  }

  const dataNode = document.getElementById('server-data')

  if (!dataNode) {
    console.error('server-data: element not found')
    return serverData
  }

  try {
    const serverDataString = dataNode.innerHTML
    Object.assign(serverData, JSON.parse(serverDataString))

    if (dataNode.parentElement) {
      dataNode.parentElement.removeChild(dataNode) // cleanup the DOM
    }

    if (process.env.NODE_ENV !== 'test') {
      // paypal-scripts will load WorldReady with content automatically during tests
      WorldReady.load(serverData.worldReady)
    }

    if (isDev()) {
      console.info('server-data', serverData)
    }
  } catch (err) {
    console.error('server-data: load', err)
  }

  return serverData
}

/**
 * Only works after server data have been initialized.
 */
function isDev() {
  return !serverData.env || serverData.env === ENV_DEVELOPMENT
}

function isProd() {
  return serverData.env === ENV_PRODUCTION
}

/**
 * To pass mock query params, i.e. _mock_[xxx] and webview query param
 *
 * to API requests.
 */
function apiPath(path) {
  path = `${serverData.requestURI}/api/${path}`

  if (!isProd()) {
    const mockParams = getMockQueryParams()
    const hasPassedStepUp = getQueryParam('rs') === '1'
    const webview = getQueryParam('webview')
    path = appendQueryParams(
      path,
      Object.assign(
        {
          webview,
        },
        mockParams,
        { hasPassedStepUp },
      ),
    )
  }
  return path
}

function hasFeature(featureName) {
  return get(serverData, `features.${featureName}`)
}

function hasOneOfFeatures(...args: any[]) {
  const features = Array.isArray(arguments[0]) ? arguments[0] : Array.from(arguments)
  return features.some((featureName) => hasFeature(featureName))
}

function getCdnAssetUrl(path = '') {
  return `https://${serverData?.cdnHost}/${path}`
}

function getUcsCdnAssetUrl(path = '') {
  return `https://${serverData?.cdnHost}/ucsnodeweb/paypal-key/${path}`
}

function getPayPalUrl(path = '') {
  return `https://www.${serverData?.payPalDomain}/${path}`
}

function getUCPConfig(key = '') {
  return get(serverData, `ucpConfig.${key}`, '')
}

function hasUCPFeature(feature = '') {
  const featureEnabled = get(serverData, `ucpConfig.features.${feature}`, false)
  return featureEnabled === true
}

function getAppConfig(key = '') {
  return get(serverData, `appConfig.${key}`)
}

/** Kraken appconfig.FEATURES (BDMC/debitcard flags), not pp-app-config or features.json */
function getFeaturesConfig(key = '') {
  return key ? get(serverData, `featuresConfig.${key}`) : get(serverData, 'featuresConfig', {})
}

function getNativeDeepLink(key) {
  return getAppConfig(`nativeDeepLinks.${key}`)
}

function isMainView() {
  return serverData.viewName === MAIN_VIEW_NAME
}

function isModalView() {
  return serverData.viewName === MODAL_VIEW_NAME
}

function getExternalLink(key = '') {
  return get(serverData, `appConfig.externalLinks.${key}`)
}

function getManageCardActions() {
  return getAppConfig(`${CONSUMER_DEBIT_CARD}.manageCardActions`) || {}
}

function getSidePanelItems() {
  return getAppConfig(`${CONSUMER_DEBIT_CARD}.sidePanel`) || {}
}

function getShortProductName(productName = CONSUMER_DEBIT_CARD) {
  return getAppConfig(`${productName}.shortProductName`) || ''
}

function getLegalProductName(productName = CONSUMER_DEBIT_CARD) {
  return getAppConfig(`${productName}.legalProductName`) || ''
}

function getReplaceCardConfig() {
  return getAppConfig(`${CONSUMER_DEBIT_CARD}.replaceCard`) || {}
}

function getViewPinConfig(productName = '') {
  return getAppConfig(`${productName}.viewPin`) || {}
}

function getActivateCardConfig(productName = CONSUMER_DEBIT_CARD) {
  return getAppConfig(`${productName}.activateCard`) || {}
}

function getFundedByAction() {
  return getAppConfig(`${CONSUMER_DEBIT_CARD}.fundedBy`) || {}
}

function getEuatToken() {
  return window?.ppdcApp?.euat || window?.PAYPAL?.checkout?.properties?.euat || ''
}

function getEcToken() {
  return window?.ppdcApp?.ecToken || window?.PAYPAL?.checkout?.properties?.ecToken || ''
}

export {
  serverData,
  initServerData,
  isDev,
  isProd,
  apiPath,
  hasFeature,
  hasOneOfFeatures,
  getCdnAssetUrl,
  getUcsCdnAssetUrl,
  getPayPalUrl,
  getUCPConfig,
  hasUCPFeature,
  getAppConfig,
  getFeaturesConfig,
  getNativeDeepLink,
  isMainView,
  isModalView,
  getExternalLink,
  getManageCardActions,
  getSidePanelItems,
  getShortProductName,
  getLegalProductName,
  getReplaceCardConfig,
  getViewPinConfig,
  getActivateCardConfig,
  getFundedByAction,
  getEuatToken,
  getEcToken,
}
/* eslint no-console:0 */
